import React, { useEffect, useState } from 'react';
import {
  Typography,
  Divider,
  TableRow,
  MenuItem,
  ListItem,
  List,
  ListItemText,
  ListItemIcon,
  Box,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  EmailTemplate,
  SendEmailTemplateMessage,
} from '../Model/EmailAdminModel';
import JoditTemplateEditor from '../../../Common/JodiEditor/JodiEditor';
import { emailadminService } from '../EmailAdministrationService';
import {
  CheckBoxItem,
  Product,
  emailFilterOptions,
} from '../../../Common/CommonModel';
import { commonService } from '../../../Common/CommonService';
import { farmService } from '../../Farm/FarmService';
import { customerService } from '../../Customer/CustomerService';
import {
  GetCustomerCheckBoxList,
  GetFarmCheckBoxList,
  GetTagCheckBoxList,
  IsValidEmail,
} from '../Common/Common';
import { globalService } from '../../UIContainer/Global/GlobalService';
import { Send } from '@mui/icons-material';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import { useHistory } from 'react-router-dom';
import { capitalize } from '../../../../Helpers/UtilityMethods';
import EmailAdminConstants from '../Common/Constants';
import { usePageTour } from '../../../Common/PopUp/PageTour';
import { SendEmailPageSteps } from '../EmailTourSteps/SendEmailPageSteps';
import { useFarmStyles as commonStyle } from '../../../../Common/Styles';
import { Loader } from '../../../../Common/Components/Loader';
import {
  CardWrapper,
  FooterContainer,
  FooterWrapper,
  PaperWrapper,
} from '../../Common/CommonComponents';
import CustomButton, {
  CustomSelect,
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import { CustomerFilter } from '../../../Common/Filters/CustomerFilter';
import { useBaseFilter } from '../../../Common/Filters/Hooks/useBaseFilter';
import { showToast } from '../../../../lib/toast';

interface SendEmailProps {
  templates: EmailTemplate[];
  products: Product[];
}

const SendEmailOps = (props: SendEmailProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedSendTo, setSelectedSendTo] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [showSendTo, setShowSendTo] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate>();
  const [farmCheckBoxList, setFarmCheckBoxList] = useState<CheckBoxItem[]>([]);
  const [customerCheckBoxList, setCustomerCheckBoxList] = useState<
    CheckBoxItem[]
  >([]);
  const [tagCheckBoxList, setTagCheckBoxList] = useState<CheckBoxItem[]>([]);
  const [email, setEmail] = useState('');
  const history = useHistory();
  const [contactsList, setContactsList] = useState<string[]>(() =>
    Array.from(new Set<string>([])),
  );
  const [loadingState, setLoadingState] = useState(false);
  const [ccs, setCCS] = useState('');
  const { classes } = commonStyle();
  usePageTour(SendEmailPageSteps);

  const baseFilters = useBaseFilter({
    farmCheckBoxList,
    setFarmCheckBoxList,
    customerCheckBoxList,
    setCustomerCheckBoxList,
    tagCheckBoxList,
    setTagCheckBoxList,
  });

  const loadFarms = (product: string) => {
    farmService
      .getFarmsByProduct(product)
      .then((farms) => setFarmCheckBoxList(GetFarmCheckBoxList(farms.Items)));
  };

  const loadCustomers = () => {
    customerService
      .getCustomersByProduct()
      .then((customers) =>
        setCustomerCheckBoxList(GetCustomerCheckBoxList(customers.Items)),
      );
  };

  const loadTags = () => {
    globalService
      .getTags()
      .then((tags) => setTagCheckBoxList(GetTagCheckBoxList(tags.Items)));
  };

  const loadContacts = async () => {
    setContactsList([]);
    setLoadingState(true);

    let promises: Promise<string[]>[] = [];

    if (selectedSendTo === 'customer') {
      promises = customerCheckBoxList
        .filter((customer) => customer.Check)
        .map((customer) =>
          customerService.getContactsByCustomerId(customer.Value),
        );
    } else if (selectedSendTo === 'farm') {
      promises = farmCheckBoxList
        .filter((farm) => farm.Check)
        .map((farm) => customerService.getContactsByfarmId(Number(farm.Value)));
    } else if (selectedSendTo === 'tags') {
      promises = tagCheckBoxList
        .filter((tag) => tag.Check)
        .map((tag) => globalService.getTagContacts(tag.Value));
    } else if (selectedSendTo === 'allcustomers') {
      const contacts = await customerService.getContacts();
      setContactsList((prevList) =>
        Array.from(new Set([...prevList, ...contacts])),
      );
      setLoadingState(false);
      return;
    }

    const allContacts: string[][] = await Promise.all(promises);
    const mergedContacts: string[] = ([] as string[]).concat(...allContacts);
    setContactsList((prevList) =>
      Array.from(new Set([...prevList, ...mergedContacts])),
    );

    setLoadingState(false);
  };

  const loadTemplate = (templateName: string, product: string) => {
    emailadminService
      .getEmailTemplate(templateName, product)
      .then((template) => setEmailTemplate(template));
  };

  useEffect(() => {
    loadData();
  }, [selectedProduct, selectedTemplate]);

  useEffect(() => {
    loadContacts();
  }, [customerCheckBoxList, farmCheckBoxList, tagCheckBoxList, selectedSendTo]);

  const loadData = () => {
    if (selectedProduct !== '' && selectedTemplate !== '') {
      loadFarms(selectedProduct);
      loadTemplate(selectedTemplate, selectedProduct);
      loadCustomers();
      loadTags();
      setShowSendTo(true);
    }
  };

  const sendEmail = () => {
    if (emailTemplate) {
      setLoadingState(true);
      const sendEmailMessage: SendEmailTemplateMessage = {
        ToEmails: contactsList,
        Subject: emailTemplate.Subject,
        HtmlContent: emailTemplate.Content,
        CCEmails: [],
      };
      if (selectedSendTo === 'email') {
        if (email.length > 0 && email !== '') {
          const emails = email.split(';');
          emails.forEach((mail) => {
            if (IsValidEmail(mail)) {
              sendEmailMessage.ToEmails.push(mail);
            } else {
              showToast({
                type: 'warning',
                title: mail + ' ' + EmailAdminConstants.MailIgnoreMessage,
              });
            }
          });
        }
      }
      if (ccs.length > 0 && ccs !== '') {
        const ccmails = ccs.split(';');
        if (ccmails.length > 0) {
          ccmails.forEach((cc) => {
            if (IsValidEmail(cc)) {
              sendEmailMessage.CCEmails.push(cc);
            } else {
              showToast({
                type: 'warning',
                title: cc + ' ' + EmailAdminConstants.MailIgnoreMessage,
              });
            }
          });
        }
      }

      if (sendEmailMessage.ToEmails.length > 0) {
        emailadminService.sendEmailWithTemplate(sendEmailMessage).then(() => {
          showToast({
            type: 'success',
            title: EmailAdminConstants.EmailSentSuccessfully,
          });
          setLoadingState(false);
          history.push('/emailadministration');
        });
      } else {
        setLoadingState(false);
        showToast({
          type: 'info',
          title: EmailAdminConstants.EmailEmptyWarning,
        });
      }
    }
  };

  return (
    <>
      <Loader loading={loadingState} />
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          <CardWrapper>
            <Typography variant="h6">Basic Information</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography variant="body1">Product</Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <CustomSelect
                  value={selectedProduct}
                  onChange={(e) => {
                    setSelectedProduct(e.target.value);
                  }}
                  select
                >
                  {props.products.map((product) => (
                    <MenuItem key={product.Id} value={product.Id}>
                      {product.Id}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid2>
            </Grid2>

            {selectedProduct && (
              <Grid2 container spacing={2} alignItems="center" mt={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body1">Template Name</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomSelect
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    {props.templates
                      .filter(
                        (template) => template.Product === selectedProduct,
                      )
                      .map((template) => (
                        <MenuItem key={template.Name} value={template.Name}>
                          {template.Name}
                        </MenuItem>
                      ))}
                  </CustomSelect>
                </Grid2>
              </Grid2>
            )}
            {showSendTo && (
              <>
                <Grid2 container spacing={2} alignItems="center" mt={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">Send To</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomSelect
                      value={selectedSendTo}
                      onChange={(e) => setSelectedSendTo(e.target.value)}
                    >
                      {emailFilterOptions.map((to) => (
                        <MenuItem key={to.Text} value={to.Value}>
                          {to.Text}
                        </MenuItem>
                      ))}
                    </CustomSelect>
                  </Grid2>
                </Grid2>
                {emailTemplate && (
                  <Grid2 container spacing={2} alignItems="center" mt={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body1">Email Subject</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <CustomTextField
                        multiline
                        maxRows={3}
                        value={emailTemplate?.Subject}
                        onChange={(e) =>
                          setEmailTemplate({
                            ...emailTemplate,
                            Subject: e.target.value,
                          })
                        }
                      />
                    </Grid2>
                  </Grid2>
                )}
                <Grid2 container spacing={2} alignItems="center" mt={2}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body1">Additional Emails</Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <CustomTextField
                      helperText={EmailAdminConstants.CcHelperText}
                      multiline
                      maxRows={3}
                      value={ccs}
                      onChange={(e) => setCCS(e.target.value)}
                    />
                  </Grid2>
                </Grid2>
              </>
            )}
          </CardWrapper>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          {selectedSendTo && selectedSendTo !== 'allcustomers' && (
            <CardWrapper>
              <Typography variant="h6">{capitalize(selectedSendTo)}</Typography>
              <Divider sx={{ mb: 3 }} />
              <Box className={classes.box}>
                <TableRow>
                  {selectedSendTo && (
                    <CustomerFilter
                      FilterMapping={baseFilters}
                      email={email}
                      setEmail={setEmail}
                      filterOption={selectedSendTo}
                    />
                  )}
                </TableRow>
              </Box>
            </CardWrapper>
          )}
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
          {selectedSendTo && selectedSendTo !== 'email' && (
            <CardWrapper>
              <Typography variant="h6">Email Recipients (Bcc)</Typography>
              <Divider sx={{ mb: 3 }} />
              <Box className={classes.box}>
                <List sx={{ padding: 2 }}>
                  {loadingState ? (
                    <Loader loading={loadingState} />
                  ) : (
                    contactsList.map((contact) => (
                      <ListItem key={contact}>
                        <ListItemIcon>
                          <ContactMailIcon />
                        </ListItemIcon>
                        <ListItemText primary={contact} />
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            </CardWrapper>
          )}
        </Grid2>
        {emailTemplate && (
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
            <CardWrapper sx={{ padding: 1 }}>
              <JoditTemplateEditor
                emailtemplate={emailTemplate}
                setEmailTemplate={
                  setEmailTemplate as React.Dispatch<
                    React.SetStateAction<EmailTemplate | undefined>
                  >
                }
              />
            </CardWrapper>
          </Grid2>
        )}
      </Grid2>
      <FooterWrapper>
        <FooterContainer>
          <Grid2 size={{ xs: 12, sm: 'auto' }}>
            <CustomButton
              startIcon={<Send />}
              onClick={sendEmail}
              label="send"
            />
          </Grid2>
        </FooterContainer>
      </FooterWrapper>
    </>
  );
};

const SendEmailPage = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    Init();
  }, []);

  const Init = () => {
    loadTemplate();
    loadProducts();
  };

  const loadTemplate = () => {
    emailadminService
      .getEmailTemplates()
      .then((templates) => setTemplates(templates.Items));
  };

  const loadProducts = () => {
    commonService.getProducts().then((products) => setProducts(products.Items));
  };

  return (
    <div>
      <PaperWrapper>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Send Email
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <SendEmailOps templates={templates} products={products} />
      </PaperWrapper>
    </div>
  );
};

export default SendEmailPage;
